/**
 * Runs a tour with driver.js.
 *
 * driver.js and its stylesheet are imported dynamically. Most sessions never
 * open a tour, and this keeps ~10KB of library and CSS out of the bundle that
 * a cashier downloads before the first sale of the day.
 */

import type { Tour } from './types';

const SEEN_PREFIX = 'ophth_tour_seen_';

/** Has this tour already run on this device? */
export function tourSeen(id: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + id) === '1';
  } catch {
    // Private browsing: treat as unseen. Showing a tour twice is a smaller
    // failure than never showing it.
    return false;
  }
}

export function markTourSeen(id: string): void {
  try {
    localStorage.setItem(SEEN_PREFIX + id, '1');
  } catch { /* see tourSeen */ }
}

export function resetAllTours(): void {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(SEEN_PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* see tourSeen */ }
}

/**
 * Start a tour. Resolves once it has been launched, not when it finishes.
 *
 * Steps pointing at elements that are not on screen are dropped: the UI
 * differs by role, and most of these pages show an empty state instead of a
 * table until there is data. Highlighting nothing would be worse than saying
 * nothing.
 */
export async function runTour(tour: Tour): Promise<void> {
  const steps = tour.steps
    .map((s) => {
      if (!s.el || document.querySelector(s.el)) return s;
      // Element not on screen: keep the words, drop the pointer — or drop the
      // step entirely when it only made sense against that control.
      return s.keepIfMissing ? { ...s, el: undefined, side: undefined, align: undefined } : null;
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);
  if (steps.length === 0) return;

  const [{ driver }] = await Promise.all([
    import('driver.js'),
    import('driver.js/dist/driver.css'),
  ]);

  driver({
    showProgress: steps.length > 1,
    progressText: '{{current}} dari {{total}}',
    nextBtnText: 'Lanjut',
    prevBtnText: 'Kembali',
    doneBtnText: 'Selesai',
    popoverClass: 'ophth-tour',
    allowClose: true,
    overlayOpacity: 0.6,
    stagePadding: 6,
    stageRadius: 8,
    onDestroyed: () => markTourSeen(tour.id),
    steps: steps.map((s) => ({
      element: s.el,
      popover: {
        title: s.title,
        description: s.body,
        side: s.side,
        align: s.align,
      },
    })),
  }).drive();
}
